/**
 * Impersonation Banner
 * Shows when an admin is impersonating a user.
 * Non-dismissable - must click Stop Impersonating.
 */
import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, X } from "lucide-react";

export function ImpersonationBanner() {
  const { t } = useTranslation("common");
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if there's an active impersonation
  const impersonation = useQuery(
    api.admin.impersonation.getActiveImpersonation,
    {}
  );

  // Don't show if no active impersonation
  if (!impersonation) {
    return null;
  }

  const handleStopImpersonation = async () => {
    if (stopping) return;

    setStopping(true);
    setError(null);

    try {
      const response = await fetch("/api/admin/stop-impersonation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          impersonationId: impersonation.impersonationId,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to stop impersonation");
      }

      // Reload page to restore admin session
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      setStopping(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-warning-muted text-warning-muted-foreground">
      <div className="mx-auto max-w-7xl px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">
              {t("impersonation.banner", {
                name: impersonation.targetUser?.name || "Unknown",
                email: impersonation.targetUser?.email || "",
              })}
            </span>
            <span className="text-sm opacity-75">
              {t("impersonation.adminOpsDisabled")}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {error && <span className="text-sm text-error-muted-foreground">{error}</span>}
            <button
              onClick={handleStopImpersonation}
              disabled={stopping}
              className="inline-flex items-center gap-2 rounded-md bg-foreground/10 px-4 py-1.5 text-sm font-medium hover:bg-foreground/20 disabled:opacity-50"
            >
              {stopping ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              {t("impersonation.stop")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
